/**
 * UserListItem - Shared component for displaying users consistently
 *
 * Supports both:
 * - Platform (table row variant) - PlatformUsers.tsx
 * - Settings (list item variant) - UserManagementRBAC.tsx
 *
 * Features:
 * - Gradient avatar with initials
 * - Role badges with fallback to legacy role (fixes "No roles assigned" bug)
 * - Status indicator (active/inactive)
 * - Selectable rows for bulk actions
 * - Customizable action buttons via renderActions prop
 */

import React, { ReactNode } from 'react';
import {
  UserData,
  getUserDisplayRoles,
  getUserFullName,
  getUserInitials,
  getUserStatus,
  getAvatarGradient,
  DisplayRole,
  UserStatus
} from './types';

// ============================================================================
// Sub-Components
// ============================================================================

interface GradientAvatarProps {
  user: UserData;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Gradient avatar with user initials
 */
export function GradientAvatar({ user, size = 'md' }: GradientAvatarProps) {
  const initials = getUserInitials(user);
  const gradient = getAvatarGradient(user.id);

  const sizeClasses = {
    sm: 'h-8 w-8 text-xs',
    md: 'h-10 w-10 text-sm',
    lg: 'h-12 w-12 text-base'
  };

  return (
    <div
      className={`${sizeClasses[size]} rounded-full bg-gradient-to-br ${gradient.from} ${gradient.to} flex items-center justify-center text-white font-semibold flex-shrink-0`}
    >
      {initials}
    </div>
  );
}

interface RoleBadgeProps {
  role: DisplayRole;
  size?: 'sm' | 'md';
}

/**
 * Role badge with colored background
 */
export function RoleBadge({ role, size = 'sm' }: RoleBadgeProps) {
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm'
  };

  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ${sizeClasses[size]} ${role.bg} ${role.color}`}
    >
      {role.displayName}
    </span>
  );
}

interface StatusBadgeProps {
  status: UserStatus;
  size?: 'sm' | 'md';
}

/**
 * User status badge (Active/Inactive)
 */
export function StatusBadge({ status, size = 'sm' }: StatusBadgeProps) {
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm'
  };

  const statusStyles: Record<UserStatus, { bg: string; text: string; label: string }> = {
    active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
    inactive: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Inactive' },
    deleted: { bg: 'bg-red-100', text: 'text-red-800', label: 'Deleted' },
    pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' }
  };

  const style = statusStyles[status];

  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ${sizeClasses[size]} ${style.bg} ${style.text}`}
    >
      {style.label}
    </span>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export interface UserListItemProps {
  /** User data (supports both legacy and RBAC formats) */
  user: UserData;

  /** Display variant */
  variant?: 'table-row' | 'list-item';

  /** Show organization name (Platform view) */
  showOrganization?: boolean;

  /** Show department from profile (Settings view) */
  showDepartment?: boolean;

  /** Show created date */
  showCreatedDate?: boolean;

  /** Show user status badge */
  showStatus?: boolean;

  /** Enable row selection */
  selectable?: boolean;

  /** Current selection state */
  selected?: boolean;

  /** Selection change callback */
  onSelectionChange?: (userId: string, selected: boolean) => void;

  /** Click handler for the entire row/item */
  onClick?: (user: UserData) => void;

  /** Custom action buttons renderer */
  renderActions?: (user: UserData) => ReactNode;

  /** Additional class names */
  className?: string;
}

/**
 * UserListItem - Unified user display component
 */
export function UserListItem({
  user,
  variant = 'list-item',
  showOrganization = false,
  showDepartment = false,
  showCreatedDate = false,
  showStatus = true,
  selectable = false,
  selected = false,
  onSelectionChange,
  onClick,
  renderActions,
  className = ''
}: UserListItemProps) {
  const fullName = getUserFullName(user);
  const roles = getUserDisplayRoles(user);
  const status = getUserStatus(user);
  const department = user.profile?.department;
  const organizationName = 'organizationName' in user ? user.organizationName : undefined;

  const handleSelectionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    onSelectionChange?.(user.id, e.target.checked);
  };

  const handleClick = () => {
    onClick?.(user);
  };

  // Format date for display
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // =========================================================================
  // Table Row Variant (Platform)
  // =========================================================================
  if (variant === 'table-row') {
    return (
      <tr
        className={`hover:bg-gray-50 transition-colors ${onClick ? 'cursor-pointer' : ''} ${className}`}
        onClick={onClick ? handleClick : undefined}
      >
        {/* Selection Checkbox */}
        {selectable && (
          <td className="px-4 py-3">
            <input
              type="checkbox"
              checked={selected}
              onChange={handleSelectionChange}
              onClick={e => e.stopPropagation()}
              className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
          </td>
        )}

        {/* User Info */}
        <td className="px-4 py-3">
          <div className="flex items-center gap-3">
            <GradientAvatar user={user} size="md" />
            <div className="min-w-0">
              <p className="font-medium text-gray-900 truncate">{fullName}</p>
              <p className="text-sm text-gray-500 truncate">{user.email}</p>
            </div>
          </div>
        </td>

        {/* Organization (Platform only) */}
        {showOrganization && (
          <td className="px-4 py-3">
            <span className="text-sm text-gray-600">{organizationName || '-'}</span>
          </td>
        )}

        {/* Role(s) */}
        <td className="px-4 py-3">
          <div className="flex flex-wrap gap-1">
            {roles.length > 0 ? (
              roles.map((role, idx) => (
                <RoleBadge key={`${role.name}-${idx}`} role={role} />
              ))
            ) : (
              <span className="text-sm text-gray-400 italic">No role</span>
            )}
          </div>
        </td>

        {/* Status */}
        {showStatus && (
          <td className="px-4 py-3">
            <StatusBadge status={status} />
          </td>
        )}

        {/* Created Date */}
        {showCreatedDate && (
          <td className="px-4 py-3">
            <span className="text-sm text-gray-500">{formatDate(user.createdAt)}</span>
          </td>
        )}

        {/* Actions */}
        {renderActions && (
          <td className="px-4 py-3">
            <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
              {renderActions(user)}
            </div>
          </td>
        )}
      </tr>
    );
  }

  // =========================================================================
  // List Item Variant (Settings)
  // =========================================================================
  return (
    <div
      className={`flex items-center justify-between p-4 hover:bg-gray-50 transition-colors ${onClick ? 'cursor-pointer' : ''} ${className}`}
      onClick={onClick ? handleClick : undefined}
    >
      {/* Left: User Info */}
      <div className="flex items-center gap-4 min-w-0 flex-1">
        {/* Selection Checkbox */}
        {selectable && (
          <input
            type="checkbox"
            checked={selected}
            onChange={handleSelectionChange}
            onClick={e => e.stopPropagation()}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
        )}

        {/* Avatar */}
        <GradientAvatar user={user} size="md" />

        {/* Name & Email */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="font-medium text-gray-900 truncate">{fullName}</p>
            {showStatus && status !== 'active' && (
              <StatusBadge status={status} size="sm" />
            )}
          </div>
          <p className="text-sm text-gray-500 truncate">{user.email}</p>
          {showDepartment && department && (
            <p className="text-xs text-gray-400 truncate">{department}</p>
          )}
        </div>
      </div>

      {/* Right: Roles & Actions */}
      <div className="flex items-center gap-4 flex-shrink-0">
        {/* Role Badges */}
        <div className="flex flex-wrap gap-1 justify-end">
          {roles.length > 0 ? (
            roles.map((role, idx) => (
              <RoleBadge key={`${role.name}-${idx}`} role={role} />
            ))
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
              No role
            </span>
          )}
        </div>

        {/* Action Buttons */}
        {renderActions && (
          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
            {renderActions(user)}
          </div>
        )}
      </div>
    </div>
  );
}

export default UserListItem;
